import { useState, useContext, useRef, useEffect } from "react";
import Modal from "react-bootstrap/Modal";
import Spinner from "react-bootstrap/Spinner";
import { useQueryClient } from "@tanstack/react-query";
import { FileSubmissionsContext } from "@/components/layout/FileSubmissionsTracker";
import { useParams, useRouteContext } from "@tanstack/react-router";
import {
  useChildData,
  useCoreData,
  useChildEntities,
  useLeaData,
} from "@/queries";
import { useAuthenticatedSuspenseQuery } from "@/queries/shared";
import {
  CSPPIDMatchLEA,
  CSPPIDMatchAction,
  MatchActionType,
  MatchLEAStatus,
  csppidMatchMutationFunctions,
} from "@/models/mid";
import { type FAMI } from "@/models/fami";
import { type CHLD } from "@/models/chld";
import { type LANG, type SLNG, type ENRL, type CLEN } from "@/models";
import Popup from "@/components/shared/Popup";
import { useConfirmDialogFocus } from "@/components/shared/useConfirmDialogFocus";
import KendoTableClientState from "@/components/shared/tables/KendoTableClientState";
import { type GridColumnProps } from "@progress/kendo-react-grid";
import {
  FiFileText,
  FiClock,
  FiGitMerge,
  FiX,
  FiRotateCcw,
  FiCheckCircle,
  FiInfo,
} from "react-icons/fi";
import { useRecordPermissions } from "@/components/discrepancies/data-discrepancies/hooks/useRecordPermissions";
import { useMidActionPolling } from "./hooks/useMidActionPolling";
import { BlockedDeletionDialog } from "@/components/shared/BlockedDeleteDialog";
import { useBlockedChildDeletion } from "@/components/records/child/hooks/useBlockedChildDeletion";
import { getChildBlockedGroups } from "@/components/records/child/utils/getChildBlockedGroups";

export { NoAccessMessage } from "@/components/discrepancies/data-discrepancies/DiscrepancyPopup";

type MIDDetailPopupProps = {
  matchLEA: CSPPIDMatchLEA;
  onClose: () => void;
  onActionComplete?: () => void;
};

const STATUS_LABELS: Record<number, string> = {
  [MatchLEAStatus.UNRESOLVED_ACTIVE_LEA]: "Unresolved",
  [MatchLEAStatus.UNRESOLVED_INACTIVE_LEA]: "Unresolved (Inactive)",
  [MatchLEAStatus.RESOLVED_MERGED]: "Merged",
  [MatchLEAStatus.RESOLVED_REJECTED]: "Not a Match",
  [MatchLEAStatus.RESOLVED_NO_LONGER_USED]: "No Longer Used",
};

const ACTION_TYPE_LABELS: Record<number, string> = {
  0: "Created",
  [MatchActionType.MERGE]: "Merge", // overridden to "Resolve" for Internal MIDs
  [MatchActionType.REJECT]: "Flagged",
  [MatchActionType.UNRESOLVE]: "Needs Review",
  [MatchActionType.SYSTEM_MERGE]: "System Merge",
  [MatchActionType.SYSTEM_CLEANUP]: "System Cleanup",
  [MatchActionType.UNMERGE]: "Unmerged",
};

const historyColumns: GridColumnProps[] = [
  { id: "MIDType", field: "MIDType", title: "MID Type" },
  { id: "Action", field: "Action", title: "Activity Type" },
  { id: "Status", field: "Status", title: "MID Status" },
  { id: "PrimaryCSPPID", field: "PrimaryCSPPID", title: "Original CSPPID" },
  {
    id: "SecondaryCSPPID",
    field: "SecondaryCSPPID",
    title: "Potential Duplicate",
  },
  { id: "UserID", field: "UserID", title: "Username" },
  { id: "CreateDate", field: "CreateDate", title: "Timestamp" },
];

const getActiveFAMIs = (famis: FAMI[] | undefined): FAMI[] =>
  (famis ?? []).filter((f) => !f.EffectiveEndDate);

const formatHeadOfHousehold = (activeFAMIs: FAMI[], slot: 1 | 2): string => {
  if (activeFAMIs.length === 0) return "—";
  if (activeFAMIs.length > 1) return slot === 1 ? "Multiple records" : "—";

  const f = activeFAMIs[0];
  const first =
    slot === 1 ? f.HeadOfHousehold1FirstName : f.HeadOfHousehold2FirstName;
  const last =
    slot === 1 ? f.HeadOfHousehold1LastName : f.HeadOfHousehold2LastName;
  const name = `${first ?? ""} ${last ?? ""}`.trim();
  return name || "—";
};

function ComparisonRow({
  label,
  primary,
  secondary,
}: {
  label: string;
  primary: string;
  secondary: string;
}) {
  const isDifferent =
    primary !== "—" &&
    secondary !== "—" &&
    primary.toLowerCase() !== secondary.toLowerCase();

  const cellClass = `mid-detail-comparison-cell${isDifferent ? " mid-detail-comparison-cell--different" : ""}`;

  return (
    <tr>
      <td className="mid-detail-comparison-label">{label}</td>
      <td className={`${cellClass} mid-detail-comparison-cell--primary`}>
        {primary}
      </td>
      <td className={cellClass}>{secondary}</td>
    </tr>
  );
}

type Tab = "details" | "history";

function TabButton({
  tab,
  activeTab,
  icon,
  label,
  onClick,
}: {
  tab: Tab;
  activeTab: Tab;
  icon: React.ReactNode;
  label: string;
  onClick: () => void;
}) {
  const isActive = activeTab === tab;
  return (
    <button
      type="button"
      role="tab"
      aria-selected={isActive}
      onClick={onClick}
      className={`mid-detail-tab${isActive ? " mid-detail-tab--active" : ""}`}
    >
      {icon}
      {label}
    </button>
  );
}

function MIDConfirmModal({
  titleId,
  title,
  message,
  cancelLabel,
  confirmLabel,
  onCancel,
  onConfirm,
}: {
  titleId: string;
  title: string;
  message: string;
  cancelLabel: string;
  confirmLabel: string;
  onCancel: () => void;
  onConfirm: () => void;
}) {
  const cancelRef = useRef<HTMLButtonElement>(null);
  const confirmRef = useRef<HTMLButtonElement>(null);
  const messageId = `${titleId}-message`;
  const handleKeyDown = useConfirmDialogFocus(cancelRef, confirmRef, onCancel);

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-labelledby={titleId}
      aria-describedby={messageId}
      onKeyDown={handleKeyDown}
      className="mid-detail-confirm-overlay"
    >
      <div className="mid-detail-confirm-dialog">
        <h5 id={titleId} className="mid-detail-confirm-title">
          {title}
        </h5>
        <p id={messageId} className="mid-detail-confirm-message">
          {message}
        </p>
        <div className="mid-detail-confirm-actions">
          <button
            ref={cancelRef}
            onClick={onCancel}
            className="mid-detail-confirm-btn-cancel"
          >
            {cancelLabel}
          </button>
          <button
            ref={confirmRef}
            onClick={onConfirm}
            className="mid-detail-confirm-btn-confirm"
          >
            {confirmLabel}
          </button>
        </div>
      </div>
    </div>
  );
}

export function MIDDetailPopupError({
  error,
  onClose,
}: {
  error: Error | null;
  onClose: () => void;
}) {
  return (
    <Modal show={true} onHide={onClose} size="lg" centered scrollable>
      <div className="modal-header d-block">
        <div className="d-flex align-items-start justify-content-between">
          <span className="mid-detail-modal-title">Unable to Load Record</span>
          <button
            type="button"
            className="btn-close mid-detail-close-btn"
            aria-label="Close"
            onClick={onClose}
          />
        </div>
      </div>
      <Modal.Body>
        <div className="d-flex flex-column align-items-center justify-content-center py-5 gap-3 text-center">
          <div className="mid-detail-error-icon">
            <span className="mid-detail-error-icon-mark">!</span>
          </div>
          <p className="mid-detail-error-title">
            Something went wrong loading this record.
          </p>
          <p className="mid-detail-error-message">
            {error?.message ??
              "An unexpected error occurred. Please try again."}
          </p>
          <button
            type="button"
            className="btn btn-outline-secondary mt-2"
            onClick={onClose}
          >
            Close
          </button>
        </div>
      </Modal.Body>
    </Modal>
  );
}

export function MIDDetailPopupSkeleton({ onClose }: { onClose: () => void }) {
  return (
    <Modal show={true} onHide={onClose} size="lg" centered scrollable>
      <div className="modal-header d-block">
        <div className="d-flex align-items-start justify-content-between">
          <span className="mid-detail-modal-title">Loading...</span>
          <button
            type="button"
            className="btn-close mid-detail-close-btn"
            aria-label="Close"
            onClick={onClose}
          />
        </div>
      </div>
      <Modal.Body>
        <div className="d-flex align-items-center justify-content-center gap-3 mid-detail-skeleton-body">
          <div
            className="spinner-border spinner-border-sm mid-detail-skeleton-spinner"
            role="status"
            aria-hidden="true"
          />
          <span className="mid-detail-skeleton-text">
            Loading record details...
          </span>
        </div>
      </Modal.Body>
    </Modal>
  );
}

export function MIDDetailPopup({
  matchLEA,
  onClose,
  onActionComplete,
}: MIDDetailPopupProps) {
  const [activeTab, setActiveTab] = useState<Tab>("details");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showMergeConfirm, setShowMergeConfirm] = useState(false);
  const [showBlockedDeletion, setShowBlockedDeletion] = useState(false);

  const params = useParams({ from: "/$leaid" });
  const leaid = params.leaid as string;
  const matchid = String(matchLEA.CSPPIDMatchId);

  const match = matchLEA.Match;
  const coreData = useCoreData();
  const leaContext = useRouteContext({ from: "/$leaid" });
  const leaList = useChildEntities("0-").data;
  const queryClient = useQueryClient();

  const leaData = useLeaData(leaContext.currentUserOrg.esiIdPath).data;

  const { canEditRecord } = useRecordPermissions();
  const canEditCHLD = canEditRecord("CHLD");

  const getPreschoolName = (child: CHLD | undefined): string => {
    if (!child) return "—";
    if (child.PreschoolName) return child.PreschoolName;
    const code = child.PreschoolCode;
    if (!code) return "—";
    return (
      leaContext.currentOrgPreschools?.find((p) => p.PreschoolCDSCode === code)
        ?.PreschoolName ?? code
    );
  };

  const primaryChildData = useChildData(match?.CSPPID ?? "").data;
  const secondaryChildData = useChildData(match?.MatchedCSPPID ?? "").data;

  const { data: history, error: historyError } = useAuthenticatedSuspenseQuery<
    CSPPIDMatchAction[]
  >(`/json/csppid_match/${leaid}/${matchid}`, ["match", leaid, matchid]);

  const { showToast } = useContext(FileSubmissionsContext);
  const pendingActionRef = useRef<MatchActionType | null>(null);
  const processingRef = useRef<HTMLDivElement>(null);

  const polling = useMidActionPolling(leaid, matchid);

  const primaryCHLD = primaryChildData?.chld?.[0];
  const secondaryCHLD = secondaryChildData?.chld?.[0];

  const primaryActiveFAMIs = getActiveFAMIs(primaryChildData?.fami);
  const secondaryActiveFAMIs = getActiveFAMIs(secondaryChildData?.fami);

  const duplicateCSPPID = match?.MatchedCSPPID ?? "";
  const duplicateChldRecords: CHLD[] = (leaData?.chld ?? []).filter(
    (c) => c.CSPPID === duplicateCSPPID
  );
  const duplicateLocalFamilyIds = new Set(
    (secondaryChildData?.fami ?? [])
      .map((f) => f.LocalFamilyId)
      .filter((id): id is string => !!id)
  );
  const duplicateFamiRecords: FAMI[] =
    duplicateLocalFamilyIds.size > 0
      ? (leaData?.fami ?? []).filter((f) =>
          duplicateLocalFamilyIds.has(f.LocalFamilyId)
        )
      : [];
  const duplicateLangRecords: LANG[] = (leaData?.lang ?? []).filter(
    (r) => r.CSPPID === duplicateCSPPID
  );
  const duplicateSlngRecords: SLNG[] = (leaData?.slng ?? []).filter(
    (r) => r.CSPPID === duplicateCSPPID
  );
  const duplicateEnrlRecords: ENRL[] = (leaData?.enrl ?? []).filter(
    (r) => r.CSPPID === duplicateCSPPID
  );
  const duplicateClenRecords: CLEN[] = (leaData?.clen ?? []).filter(
    (r) => r.CSPPID === duplicateCSPPID
  );

  const hasDuplicateRecords =
    duplicateChldRecords.length > 0 ||
    duplicateFamiRecords.length > 0 ||
    duplicateLangRecords.length > 0 ||
    duplicateSlngRecords.length > 0 ||
    duplicateEnrlRecords.length > 0 ||
    duplicateClenRecords.length > 0;

  const {
    pendingChldDeletes,
    pendingFamiDeletes,
    pendingLangDeletes,
    pendingSlngDeletes,
    pendingEnrlDeletes,
    pendingClenDeletes,
    addFamiDelete,
    addLangDelete,
    addSlngDelete,
    addEnrlDelete,
    addClenDelete,
    handleBlockedDeleteRecords,
    deletingRecords: isAnyBulkDeleting,
    reset: resetBlockedDeletion,
  } = useBlockedChildDeletion({
    leaData,
    chldRecords: duplicateChldRecords,
    famiRecords: duplicateFamiRecords,
    langRecords: duplicateLangRecords,
    slngRecords: duplicateSlngRecords,
    enrlRecords: duplicateEnrlRecords,
    clenRecords: duplicateClenRecords,
    blockedDeletionOpen: showBlockedDeletion,
  });

  const hasRelatedRecords =
    duplicateFamiRecords.length > 0 ||
    duplicateLangRecords.length > 0 ||
    duplicateSlngRecords.length > 0 ||
    duplicateEnrlRecords.length > 0 ||
    duplicateClenRecords.length > 0;

  const rawBlockedGroups = getChildBlockedGroups({
    extraChldRecords: duplicateChldRecords,
    pendingChldDeletes,
    famiRecords: duplicateFamiRecords,
    langRecords: duplicateLangRecords,
    slngRecords: duplicateSlngRecords,
    enrlRecords: duplicateEnrlRecords,
    clenRecords: duplicateClenRecords,
    pendingFamiDeletes,
    pendingLangDeletes,
    pendingSlngDeletes,
    pendingEnrlDeletes,
    pendingClenDeletes,
    addFamiDelete,
    addLangDelete,
    addSlngDelete,
    addEnrlDelete,
    addClenDelete,
    leaContext,
    leaData,
  });

  const blockedGroups = hasRelatedRecords
    ? rawBlockedGroups.map((g) =>
        g.key === "chld"
          ? {
              ...g,
              records: g.records.map((r) => ({
                ...r,
                disabled: true,
                disabledReason: "Delete related records first",
              })),
            }
          : g
      )
    : rawBlockedGroups;

  const getCountryText = (code: string | undefined) => {
    if (!code) return "—";
    return (
      coreData.countryList?.find((c: any) => c.Value === code)?.Text ?? code
    );
  };

  const getStateText = (code: string | undefined) => {
    if (!code) return "—";
    return code.split("-").slice(1).join("-") || code;
  };

  const getLEAName = (code: string | undefined) => {
    if (!code) return "—";
    return leaList?.find((x: any) => x.CDSCode === code)?.LEAName ?? code;
  };

  const val = (v: string | undefined | null) => v || "—";

  const isInternal = matchLEA.IsInternal;
  const midType: "Internal" | "External" = isInternal ? "Internal" : "External";

  // Internal MIDs label RESOLVED_MERGED as "Resolved" rather than "Merged"
  const getStatusLabel = (status: number): string => {
    if (isInternal && status === MatchLEAStatus.RESOLVED_MERGED)
      return "Resolved";
    return STATUS_LABELS[status] ?? String(status);
  };

  const getActionLabel = (action: number): string => {
    if (isInternal && action === MatchActionType.MERGE) return "Resolve";
    return ACTION_TYPE_LABELS[action] ?? String(action);
  };

  const showInternalWarning =
    isInternal &&
    matchLEA.Status !== MatchLEAStatus.RESOLVED_MERGED &&
    matchLEA.Status !== MatchLEAStatus.RESOLVED_NO_LONGER_USED;

  // Resets all in-flight UI state after a failed action so the spinner stops
  // and the user can retry, instead of hanging forever. Used as the mutation's
  // onError callback (e.g. when the /add endpoint returns a 500) and is also
  // safe to call from a polling-timeout path.
  const handleActionFailure = (
    title = "Action Failed",
    message = "Something went wrong processing this action. Please try again."
  ) => {
    showToast("error", title, message);
    setIsSubmitting(false);
    setShowMergeConfirm(false);
    pendingActionRef.current = null;
    polling.reset();
  };

  const mutateFns = csppidMatchMutationFunctions(
    (resp) => {
      const action = resp as unknown as CSPPIDMatchAction | null;

      if (!action || typeof action.Id !== "number") {
        // Backend rejected the action up front (e.g. an existing CREATED
        // action on the same MatchLEA). Reset UI via the shared helper, then
        // refresh the parent list so it reflects any server-side state change
        // — mirroring the async "not_allowed" path in the polling effect.
        handleActionFailure(
          "Action Not Allowed",
          "The backend could not process this action."
        );
        onActionComplete?.();
        return;
      }

      polling.startPolling(action.Id);
    },
    // onError: the request itself rejected (network error or non-2xx such as
    // the 500 from /add). Stop the spinner and tell the user.
    () => {
      handleActionFailure();
    }
  );

  useEffect(() => {
    if (polling.phase !== "complete" || !polling.result) return;

    const pendingAction = pendingActionRef.current;
    const resultStatus = polling.result.status;

    if (resultStatus === "completed") {
      if (pendingAction === MatchActionType.MERGE) {
        showToast(
          "success",
          isInternal ? "MID Resolved" : "Records Merged",
          isInternal
            ? "The MID has been marked as resolved."
            : "The records have been successfully merged."
        );
      } else if (pendingAction === MatchActionType.REJECT) {
        showToast(
          "success",
          "Marked as Not a Match",
          "This MID has been flagged as not a match."
        );
      } else if (pendingAction === MatchActionType.UNRESOLVE) {
        showToast(
          "success",
          "Marked for Review",
          "This MID has been moved back to Unresolved."
        );
      }
      onActionComplete?.();
      onClose();
    } else if (resultStatus === "not_allowed") {
      showToast(
        "error",
        "Action Not Allowed",
        "This action could not be completed in its current state."
      );

      onActionComplete?.();
      onClose();
    } else {
      showToast(
        "error",
        "Action Failed",
        "Something went wrong processing this action. Please try again."
      );
    }

    queryClient.invalidateQueries({
      queryKey: [`/json/csppid_match/${leaid}/${matchid}`],
    });

    setIsSubmitting(false);
    setShowMergeConfirm(false);
    pendingActionRef.current = null;
    polling.reset();
    // onActionComplete/onClose are referenced above; include them so the
    // effect never closes over stale versions. A full exhaustive-deps pass
    // (showToast, queryClient, leaid, matchid, isInternal) is intentionally
    // left out here to avoid pulling in the `polling` object identity.
  }, [polling.phase, polling.result, onActionComplete, onClose]);

  const primaryName = primaryCHLD
    ? `${primaryCHLD.ChildFirstName ?? ""} ${primaryCHLD.ChildLastName ?? ""}`.trim()
    : "—";
  const secondaryName = secondaryCHLD
    ? `${secondaryCHLD.ChildFirstName ?? ""} ${secondaryCHLD.ChildLastName ?? ""}`.trim()
    : "—";
  const childName = primaryName !== "—" ? primaryName : secondaryName;

  const statusLabel = getStatusLabel(matchLEA.Status);

  const canReject =
    canEditCHLD &&
    (matchLEA.Status === MatchLEAStatus.UNRESOLVED_ACTIVE_LEA ||
      matchLEA.Status === MatchLEAStatus.UNRESOLVED_INACTIVE_LEA);
  const canMerge =
    canEditCHLD &&
    (matchLEA.Status === MatchLEAStatus.UNRESOLVED_ACTIVE_LEA ||
      matchLEA.Status === MatchLEAStatus.UNRESOLVED_INACTIVE_LEA ||
      matchLEA.Status === MatchLEAStatus.RESOLVED_REJECTED);
  const canNeedsReview =
    canEditCHLD && matchLEA.Status === MatchLEAStatus.RESOLVED_REJECTED;

  const handleAction = (actionType: MatchActionType) => {
    if (!match?.Id) return;
    pendingActionRef.current = actionType;
    setIsSubmitting(true);
    mutateFns.handleSave(match.Id, actionType);
  };

  const willAutoShowResolveConfirm =
    isInternal &&
    showBlockedDeletion &&
    !hasDuplicateRecords &&
    !isAnyBulkDeleting &&
    !isSubmitting &&
    !polling.isPolling;

  const hasAutoShownResolveConfirmRef = useRef(false);

  useEffect(() => {
    if (!showBlockedDeletion) hasAutoShownResolveConfirmRef.current = false;
  }, [showBlockedDeletion]);

  useEffect(() => {
    if (willAutoShowResolveConfirm && !hasAutoShownResolveConfirmRef.current) {
      hasAutoShownResolveConfirmRef.current = true;
      setShowBlockedDeletion(false);
      setShowMergeConfirm(true);
    }
  }, [willAutoShowResolveConfirm]);

  const handleNeedsReviewClick = () => {
    handleAction(MatchActionType.UNRESOLVE);
  };

  const handleMergeClick = () => {
    if (isInternal) {
      if (hasDuplicateRecords) {
        setShowBlockedDeletion(true);
      } else {
        setShowMergeConfirm(true);
      }
      return;
    }
    setShowMergeConfirm(true);
  };

  const handleCancelMerge = () => setShowMergeConfirm(false);
  const handleConfirmMerge = () => {
    setShowMergeConfirm(false);
    handleAction(MatchActionType.MERGE);
  };

  const handleCloseBlockedDeletion = () => {
    setShowBlockedDeletion(false);
    resetBlockedDeletion();
  };

  // Terminal action from BlockedDeletionDialog once all duplicate records
  // have been cleared. Fires MERGE; the polling-completion effect handles
  // success/failure messaging and closing the popup.
  const handleBlockedDeletionResolve = () => {
    handleAction(MatchActionType.MERGE);
  };

  const historyTableData = (history ?? []).map((h) => ({
    MIDType: midType,
    Action: getActionLabel(h.Action),
    Status: getStatusLabel(h.Status),
    PrimaryCSPPID: match?.CSPPID ?? "—",
    SecondaryCSPPID: match?.MatchedCSPPID ?? "—",
    UserID: h.UserID,
    CreateDate: h.CreateDate
      ? new Date(h.CreateDate).toLocaleString("en-US", {
          month: "numeric",
          day: "numeric",
          year: "numeric",
          hour: "numeric",
          minute: "2-digit",
          hour12: true,
        })
      : "—",
  }));

  const showProcessing = isSubmitting || polling.isPolling;

  useEffect(() => {
    if (showProcessing) {
      processingRef.current?.focus();
    }
  }, [showProcessing]);

  const processingTitle = (() => {
    const pending = pendingActionRef.current;
    if (pending === MatchActionType.MERGE) {
      return isInternal ? "Resolving MID..." : "Merging records...";
    }
    if (pending === MatchActionType.REJECT) return "Marking as Not a Match...";
    if (pending === MatchActionType.UNRESOLVE) return "Marking for review...";
    return "Processing your request...";
  })();

  const blockedDeletionSubtitle = `${childName} · Duplicate CSPPID: ${match?.MatchedCSPPID ?? "—"}`;

  return (
    <>
      {(!showBlockedDeletion || willAutoShowResolveConfirm) && (
        <Popup
          show={true}
          size="lg"
          onClose={showProcessing ? () => {} : onClose}
          enforceFocus={!showMergeConfirm}
          hasButton={canMerge && !showProcessing}
          buttonText={
            <div className="d-flex align-items-center gap-2">
              {isInternal ? (
                <FiCheckCircle size={15} />
              ) : (
                <FiGitMerge size={15} />
              )}
              <span>{isInternal ? "Resolve" : "Merge Records"}</span>
            </div>
          }
          onButtonClick={handleMergeClick}
          backButtonText="Cancel"
          onBackButtonClick={onClose}
          additionalFooterButton={
            showProcessing ? undefined : canReject ? (
              <button
                type="button"
                className="btn btn-outline-danger d-flex align-items-center gap-2 mid-detail-btn-not-a-match"
                onClick={() => handleAction(MatchActionType.REJECT)}
                disabled={isSubmitting}
              >
                <FiX size={15} />
                Not a Match
              </button>
            ) : canNeedsReview ? (
              <button
                type="button"
                className="btn d-flex align-items-center gap-2 mid-detail-btn-needs-review"
                onClick={handleNeedsReviewClick}
                disabled={isSubmitting}
              >
                <FiRotateCcw size={15} />
                Needs Review
              </button>
            ) : undefined
          }
          header={
            <div className="d-flex align-items-center gap-3">
              <span className="mid-detail-header-title">
                {midType} MID: {childName}
              </span>
              <span
                className={`mid-detail-status-badge${canReject ? " mid-detail-status-badge--warn" : ""}`}
              >
                {statusLabel}
              </span>
            </div>
          }
        >
          {showProcessing ? (
            <div
              className="d-flex flex-column align-items-center justify-content-center py-5 gap-3 text-center"
              role="alert"
              tabIndex={-1}
              ref={processingRef}
            >
              <Spinner animation="border" variant="primary" />
              <p className="mid-detail-processing-title">{processingTitle}</p>
              <p className="mid-detail-processing-subtitle">
                Please do not close this window.
              </p>
            </div>
          ) : (
            <div className="mid-detail-body">
              {/* Floating Tab Bar */}
              <div className="d-flex align-items-center gap-2 mx-4 mt-3 mb-4 mid-detail-tabs">
                <TabButton
                  tab="details"
                  activeTab={activeTab}
                  icon={<FiFileText size={15} />}
                  label="Details"
                  onClick={() => setActiveTab("details")}
                />
                <TabButton
                  tab="history"
                  activeTab={activeTab}
                  icon={<FiClock size={15} />}
                  label="History"
                  onClick={() => setActiveTab("history")}
                />
              </div>

              {/* Tab Content */}
              <div className="mid-detail-tab-content">
                {activeTab === "details" && (
                  <>
                    {showInternalWarning && (
                      <div
                        role="note"
                        style={{
                          margin: "0 0 1rem 0",
                          padding: "12px 16px",
                          backgroundColor: "#FEF3C7",
                          border: "1px solid #FCD34D",
                          borderRadius: 6,
                          color: "#78350F",
                          display: "flex",
                          gap: 10,
                          alignItems: "flex-start",
                          fontSize: "0.95rem",
                          lineHeight: 1.4,
                        }}
                      >
                        <FiInfo
                          size={18}
                          style={{ flexShrink: 0, marginTop: 2 }}
                          aria-hidden="true"
                        />
                        <span>
                          Resolving an Internal MID, after determining there is
                          a match, is a two-step process: select Resolve to
                          delete all records under the duplicate CSPPID, then
                          select Resolve to remove the relationship between the
                          CICN and CSPPID.
                        </span>
                      </div>
                    )}
                    <div className="overflow-hidden mid-detail-comparison-wrapper">
                      <table className="table mb-0 mid-detail-comparison-table">
                        <thead>
                          <tr>
                            <th className="mid-detail-col-field">Field</th>
                            <th className="mid-detail-col-value">
                              Original CSPPID
                            </th>
                            <th className="mid-detail-col-value">
                              Potential Duplicate
                            </th>
                          </tr>
                        </thead>
                        <tbody>
                          <ComparisonRow
                            label="CSPPID"
                            primary={val(match?.CSPPID)}
                            secondary={val(match?.MatchedCSPPID)}
                          />
                          <ComparisonRow
                            label="CICN"
                            primary={val(primaryCHLD?.LocalChildId)}
                            secondary={val(secondaryCHLD?.LocalChildId)}
                          />
                          <ComparisonRow
                            label="LEA"
                            primary={getLEAName(primaryCHLD?.ReportingLEA)}
                            secondary={getLEAName(secondaryCHLD?.ReportingLEA)}
                          />
                          <ComparisonRow
                            label="Preschool"
                            primary={getPreschoolName(primaryCHLD)}
                            secondary={getPreschoolName(secondaryCHLD)}
                          />
                          <ComparisonRow
                            label="Program Year"
                            primary={val(primaryCHLD?.ProgramYear)}
                            secondary={val(secondaryCHLD?.ProgramYear)}
                          />
                          <ComparisonRow
                            label="Child Name"
                            primary={primaryName}
                            secondary={secondaryName}
                          />
                          <ComparisonRow
                            label="Gender"
                            primary={val(primaryCHLD?.ChildGender)}
                            secondary={val(secondaryCHLD?.ChildGender)}
                          />
                          <ComparisonRow
                            label="Date of Birth"
                            primary={val(primaryCHLD?.ChildDateOfBirth)}
                            secondary={val(secondaryCHLD?.ChildDateOfBirth)}
                          />
                          <ComparisonRow
                            label="City of Birth"
                            primary={val(primaryCHLD?.ChildCityOfBirth)}
                            secondary={val(secondaryCHLD?.ChildCityOfBirth)}
                          />
                          <ComparisonRow
                            label="State of Birth"
                            primary={getStateText(
                              primaryCHLD?.ChildStateOfBirth
                            )}
                            secondary={getStateText(
                              secondaryCHLD?.ChildStateOfBirth
                            )}
                          />
                          <ComparisonRow
                            label="Country of Birth"
                            primary={getCountryText(
                              primaryCHLD?.ChildCountryOfBirth
                            )}
                            secondary={getCountryText(
                              secondaryCHLD?.ChildCountryOfBirth
                            )}
                          />
                          <ComparisonRow
                            label="Head of Household 1"
                            primary={formatHeadOfHousehold(
                              primaryActiveFAMIs,
                              1
                            )}
                            secondary={formatHeadOfHousehold(
                              secondaryActiveFAMIs,
                              1
                            )}
                          />
                          <ComparisonRow
                            label="Head of Household 2"
                            primary={formatHeadOfHousehold(
                              primaryActiveFAMIs,
                              2
                            )}
                            secondary={formatHeadOfHousehold(
                              secondaryActiveFAMIs,
                              2
                            )}
                          />
                        </tbody>
                      </table>
                    </div>
                  </>
                )}

                {activeTab === "history" && (
                  <div className="overflow-hidden mid-history-wrapper">
                    <div className="mid-history-table">
                      <KendoTableClientState
                        data={historyTableData}
                        columns={historyColumns}
                        error={historyError}
                        filterable={false}
                      />
                    </div>
                  </div>
                )}
              </div>
            </div>
          )}
        </Popup>
      )}

      {showMergeConfirm && (
        <MIDConfirmModal
          titleId="mid-merge-confirm-title"
          title={isInternal ? "Confirm Resolve" : "Confirm Merge"}
          message={
            isInternal
              ? "There are no records under the duplicate CSPPID. Resolving will remove the relationship between the CICN and CSPPID. This action cannot be undone."
              : "Merging these records will update the CSPPID for this child on all your records to the Original CSPPID. This action cannot be undone. Are you sure you want to merge?"
          }
          cancelLabel="Cancel"
          confirmLabel={isInternal ? "Resolve" : "Merge"}
          onCancel={handleCancelMerge}
          onConfirm={handleConfirmMerge}
        />
      )}

      {showBlockedDeletion && !willAutoShowResolveConfirm && (
        <BlockedDeletionDialog
          show={showBlockedDeletion}
          onClose={handleCloseBlockedDeletion}
          title="Resolve Internal MID"
          subtitle={blockedDeletionSubtitle}
          description="Delete all records under the duplicate CSPPID below. Once they're cleared, you can complete the resolution to remove the relationship between the CICN and CSPPID."
          parentRecordKindLabel="MID"
          groups={blockedGroups}
          onDeleteRecords={handleBlockedDeleteRecords}
          onDeleteParent={handleBlockedDeletionResolve}
          deletingRecords={isAnyBulkDeleting}
          deletingParent={showProcessing}
          clearedTitle="Ready to resolve this MID"
          clearedDescription="All records under the duplicate CSPPID have been cleared. Click below to remove the relationship between the CICN and CSPPID and complete the resolution."
          finalActionLabel="Resolve"
          finalActionIcon={<FiCheckCircle aria-hidden />}
          finalConfirmTitle="Confirm Resolve"
          finalConfirmMessage="This will remove the relationship between the CICN and CSPPID, completing the resolution. This action cannot be undone."
          finalConfirmButtonText="Resolve"
        />
      )}
    </>
  );
}